import { useState, useEffect } from 'react';
import Welcome from './screens/Welcome';
import Login from './screens/Login';
import Signup from './screens/Signup';
import CreateGame from './screens/CreateGame';
import Host from './screens/Host';
import Player from './screens/Player';
import SoloPlay from './screens/SoloPlay';
import Pricing from './screens/Pricing';
import Profile from './screens/Profile';
import Admin from './screens/Admin';
import TV from './screens/TV';
import { supabase } from './supabase.js';

const OWNER_EMAIL = 'aminerhassi@gmail.com';

// Captured ONCE, the instant this file loads — before Supabase wipes the URL.
// This is how we know the user arrived by clicking a password-reset link.
const IS_RECOVERY = typeof window !== 'undefined' && window.location.hash.includes('type=recovery');

// If the URL has ?tv=ROOMCODE, this page is a TV / big-screen display.
const TV_PARAMS = (() => {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  if (!p.has('tv')) return null;
  return { room: (p.get('tv') || '').trim().toUpperCase(), lang: p.get('lang') || 'en' };
})();

// ===== Set New Password screen (shown after clicking a reset link) =====
function ResetPassword({ onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10, padding: '12px 14px',
    fontSize: 14, color: '#fff', outline: 'none',
    fontFamily: 'sans-serif', boxSizing: 'border-box',
  };

  const submit = async () => {
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) { setError(err.message); }
    else { setDone(true); window.history.replaceState({}, '', '/'); }
  };

  return (
    <div style={{ background: '#07071A', minHeight: '100vh', fontFamily: 'sans-serif', color: '#fff', padding: 20, maxWidth: 480, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20, marginBottom: 28 }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 18, color: '#fff' }}>TQ</div>
      </div>

      {done ? (
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <div style={{ fontSize: 54, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 10 }}>Password updated!</div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginBottom: 28, lineHeight: 1.6 }}>You're all set — use your new password from now on.</div>
          <button onClick={onDone} style={{ width: '100%', background: '#FFD166', color: '#000', border: 'none', borderRadius: 14, padding: 16, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>Continue →</button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 26, fontWeight: 900, marginBottom: 4 }}>Set a new password 🔑</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 24 }}>Enter a new password for your account.</div>

          {error && (
            <div style={{ background: 'rgba(239,71,111,0.13)', border: '1px solid rgba(239,71,111,0.4)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#EF476F' }}>{error}</div>
          )}

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>NEW PASSWORD</div>
            <input type={show ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>CONFIRM NEW PASSWORD</div>
            <input type={show ? 'text' : 'password'} placeholder="••••••••" value={confirm} onChange={e => setConfirm(e.target.value)} style={inputStyle} />
          </div>

          <div onClick={() => setShow(!show)} style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 24, cursor: 'pointer' }}>
            {show ? '🙈 Hide passwords' : '👁️ Show passwords'}
          </div>

          <button onClick={submit} disabled={loading} style={{ width: '100%', background: loading ? 'rgba(255,255,255,0.1)' : '#FFD166', color: loading ? 'rgba(255,255,255,0.4)' : '#000', border: 'none', borderRadius: 14, padding: 16, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            {loading ? 'Saving...' : 'Update Password'}
          </button>
        </>
      )}
    </div>
  );
}

function App() {
  const [screen, setScreen] = useState(IS_RECOVERY ? 'reset-password' : 'welcome');
  const [gameConfig, setGameConfig] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftQuestions, setDraftQuestions] = useState([]);
  const [draftGameName, setDraftGameName] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [loginIntent, setLoginIntent] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem('tabliq-lang') || 'en');

  useEffect(() => {
    localStorage.setItem('tabliq-lang', lang);
  }, [lang]);

  const checkIfPaid = (user) => {
    if (!user) return false;
    if (user.email === OWNER_EMAIL) return true;
    if (user.user_metadata?.is_pro !== true) return false;

    // Check expiration date
    const expiresAt = user.user_metadata?.pro_expires_at;
    if (expiresAt) {
      const expiry = new Date(expiresAt);
      if (expiry < new Date()) return false;
    }
    return true;
  };

  const isOwner = currentUser?.email === OWNER_EMAIL;

  const freeGameUsed = currentUser?.user_metadata?.free_game_used === true;

  const markFreeGameUsed = async () => {
    const { data } = await supabase.auth.updateUser({ data: { free_game_used: true } });
    if (data?.user) setCurrentUser(data.user);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        let user = session.user;

        if (payment === 'success') {
          setPaymentSuccess(true);
          await new Promise(r => setTimeout(r, 2000));
          const { data: refreshed } = await supabase.auth.refreshSession();
          if (refreshed?.session?.user) {
            user = refreshed.session.user;
          }
          window.history.replaceState({}, '', '/');
          setTimeout(() => setPaymentSuccess(false), 5000);
        }

        setCurrentUser(user);
        setIsPaid(checkIfPaid(user));
        setScreen(IS_RECOVERY ? 'reset-password' : 'welcome');
      } else if (IS_RECOVERY) {
        setScreen('reset-password');
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        if (session?.user) setCurrentUser(session.user);
        setScreen('reset-password');
        return;
      }
      if (session?.user) {
        setCurrentUser(session.user);
        setIsPaid(checkIfPaid(session.user));
      } else {
        setCurrentUser(null);
        setIsPaid(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    setIsPaid(checkIfPaid(user));
    setScreen(loginIntent === 'host' ? 'create' : 'welcome');
    setLoginIntent(null);
  };

  const handleSignup = (user) => {
    setCurrentUser(user);
    setIsPaid(checkIfPaid(user));
    setScreen(loginIntent === 'host' ? 'create' : 'welcome');
    setLoginIntent(null);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setIsPaid(false);
    setScreen('welcome');
  };

  const getUserName = () => {
    const meta = currentUser?.user_metadata;
    return meta?.name || currentUser?.email?.split('@')[0] || 'Host';
  };

  const getInitial = () => getUserName().charAt(0).toUpperCase();

  // ===== TV / big-screen display mode (public, no login) =====
  if (TV_PARAMS) {
    return <TV roomCode={TV_PARAMS.room} lang={TV_PARAMS.lang} />;
  }

  if (loading) {
    return (
      <div style={{ background: '#07071A', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 60, height: 60, borderRadius: 14, background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 22, color: '#fff' }}>TQ</div>
        {paymentSuccess && <div style={{ fontSize: 14, color: '#06D6A0', fontWeight: 700 }}>✅ Payment confirmed! Activating your plan...</div>}
      </div>
    );
  }
const showHeader = currentUser && ['create', 'host'].includes(screen);
  

  return (
    <div style={{ background: '#07071A', minHeight: '100vh', fontFamily: 'sans-serif' }}>

      {showPricing && (
        <Pricing
          onClose={() => setShowPricing(false)}
          onSelect={(planId) => {
            setIsPaid(true);
            setShowPricing(false);
          }}
        />
      )}

      {showProfile && (
        <Profile
          onBack={() => setShowProfile(false)}
          currentUser={currentUser}
          isPaid={isPaid}
          onUpgrade={() => {
            setShowProfile(false);
            setShowPricing(true);
          }}
        />
      )}

      {paymentSuccess && !loading && (
        <div style={{ background: 'linear-gradient(90deg, #06D6A0, #04a07a)', padding: '12px 20px', textAlign: 'center', fontFamily: 'sans-serif', fontSize: 15, fontWeight: 700, color: '#000' }}>
          🎉 Payment confirmed! Welcome to Pro!
        </div>
      )}

      {showHeader && !showProfile && (
        <div style={{ background: '#0D0D22', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 480, margin: '0 auto' }}>
          <div onClick={() => setShowProfile(true)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 16, color: '#fff' }}>
              {getInitial()}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{getUserName()}</div>
              {isPaid
                ? <div style={{ fontSize: 11, color: '#06D6A0', fontWeight: 700 }}>✅ Pro</div>
                : <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>View profile →</div>
              }
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isOwner && (
              <button onClick={() => setScreen('admin')} title="Admin: Question Bank" style={{ background: 'rgba(255,209,102,0.15)', border: '1px solid rgba(255,209,102,0.4)', color: '#FFD166', borderRadius: 20, padding: '6px 12px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                🛠 Admin
              </button>
            )}
            {!isPaid && (
              <button onClick={() => setShowPricing(true)} style={{ background: 'linear-gradient(135deg, #FFD166, #f0a500)', color: '#000', border: 'none', borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                ⭐ Upgrade
              </button>
            )}
            <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', borderRadius: 20, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
              Log out
            </button>
          </div>
        </div>
      )}

      {screen === 'reset-password' && (
        <ResetPassword onDone={() => setScreen('welcome')} />
      )}

      {screen === 'welcome' && (
        <Welcome
          onHost={() => {
            if (currentUser) { setScreen('create'); }
            else { setLoginIntent('host'); setScreen('login'); }
          }}
          onPlayer={() => setScreen('player')}
          onSolo={() => setScreen('solo')}
          onLogin={() => { setLoginIntent(null); setScreen('login'); }}
          onLogout={handleLogout}
          onProfile={() => setShowProfile(true)}
          user={currentUser}
          lang={lang}
          setLang={setLang}
        />
      )}

      {screen === 'login' && (
        <Login
          onBack={() => setScreen('welcome')}
          onLogin={handleLogin}
          onSignup={() => setScreen('signup')}
        />
      )}

      {screen === 'signup' && (
        <Signup
          onBack={() => setScreen('login')}
          onSignup={handleSignup}
          onLogin={() => setScreen('login')}
        />
      )}

      {screen === 'create' && !showProfile && (
        <CreateGame
          onBack={() => setScreen('welcome')}
          onStart={(config) => {
            if (!isPaid && !isOwner) {
              if (freeGameUsed) { setShowPricing(true); return; }
              markFreeGameUsed();
            }
            setGameConfig(config);
            setScreen('host');
          }}
          draftQuestions={draftQuestions}
          setDraftQuestions={setDraftQuestions}
          draftGameName={draftGameName}
          setDraftGameName={setDraftGameName}
          lang={lang}
        />
      )}

      {screen === 'host' && !showProfile && (
        <Host
          onBack={() => setScreen('create')}
          gameConfig={gameConfig}
          isPaid={isPaid}
          onUpgrade={() => setShowPricing(true)}
          lang={lang}
        />
      )}

      {screen === 'player' && (
        <Player onBack={() => setScreen('welcome')} lang={lang} />
      )}

      {screen === 'solo' && (
        <SoloPlay
          onBack={() => setScreen('welcome')}
          onLogin={() => setScreen('login')}
          lang={lang}
          user={currentUser}
        />
      )}

      {screen === 'admin' && isOwner && (
        <Admin onBack={() => setScreen('create')} />
      )}

    </div>
  );
}

export default App;